import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, mediumDesktop } from 'helpers/utils/screensizes';
import ProjectDetails from './components/projectDetails';
import AddProducts from './components/addProducts';
import ViewProducts from './components/viewProducts';
import ProjectSummary from './components/projectSummary';
import { useAccount, useProjectBuilder } from 'frontastic';
import Typography from 'components/commercetools-ui/atoms/typography';
import Spinner from 'components/commercetools-ui/organisms/spinner';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import ProjectLayout from './layout';
import { ctDataToFormData } from './components/data';
import { useWarnIfUnsavedChanges } from 'helpers/hooks/useWarnIfUnsavedChanges';

export interface ProjectFormData {
  projectLocation: {
    name: string;
    value: string;
  };
  projectName: string;
  projectType: {
    name: string;
    value: string;
  };
  projectStatus: {
    name: string;
    value: string;
  };
  username: string;
  selectedRoomDetails: [];
}

const ProjectBuilder = ({ data }: any) => {
  const router = useRouter();
  const { loggedIn, account } = useAccount();
  const pathname = usePathname();
  const { getProjectById } = useProjectBuilder();

  const searchParams = useSearchParams();
  const projectIdToEdit = searchParams.get('projectId') as string;

  const isCreatePage = pathname.indexOf('/create') !== -1;
  const isEditPage = pathname.indexOf('/edit') !== -1;
  const isViewPage = pathname.indexOf('/view') !== -1;
  const isSharePage = pathname.indexOf('/share') !== -1;

  const [activeTab, setActiveTab] = useState(1);
  const [loading, setLoading] = useState(true);
  const getInitialTitle = () => (isEditPage || isViewPage ? 'Edit Project' : 'Create Project');
  const [projectTitle, setProjectTitle] = useState(getInitialTitle());
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isMediumDesktop] = useMediaQuery(mediumDesktop);
  const [projectFormData, setProjectFormData] = useState<ProjectFormData | null>(null);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const isFormDirtyRef = useRef(isFormDirty);

  const [openSaveConfirmation, setOpenSaveConfirmation] = useState<boolean>(false);
  const openSaveConfirmationRef = useRef(openSaveConfirmation);
  const [tabIndexValue, setTabIndexValue] = useState(0);
  const { showModal, handleConfirmLeave, handleCancelLeave } = useWarnIfUnsavedChanges(isFormDirty);

  useEffect(()=>{
    setOpenSaveConfirmation(showModal);
  },[showModal]);
  useEffect(() => {
    if (!loggedIn && (isCreatePage || isEditPage || isViewPage || isSharePage)) {
      router.replace(`/`);
    }
    else if(!account?.firstName && (isCreatePage || isEditPage || isViewPage || isSharePage)) {
      router.replace(`/company-registration`);
    }
    else {
      setLoading(false);
    }
  }, [loggedIn]);

  const updateProjectData = (projectData: any, tabIndex: number) => {
    setProjectFormData(projectData);
    if ([3, 4].includes(tabIndex)) {
      setProjectTitle(projectData?.projectName || '');
    } else if (tabIndex === 2) {
      setProjectTitle(`Add products to ${projectData?.projectName || ''}`);
    } else {
      setProjectTitle(getInitialTitle());
    }
  };
  const fetchAndUpdateProjectData = async (shoppingListId: string, tabIndex: number) => {
    const projectEditData: any = await getProjectById({ shoppingListId });
    updateProjectData(ctDataToFormData(projectEditData?.data) ?? {}, tabIndex);
  };
  
  const handleTabClick = useCallback(
    async (tabIndex: number, productData?: any) => {
      setTabIndexValue(tabIndex);
      if (isFormDirtyRef.current && !openSaveConfirmationRef.current) {
        setOpenSaveConfirmation(true);
        return;
      }
      if (productData && !projectIdToEdit) {
        await fetchAndUpdateProjectData(productData?.id, tabIndex);
      } else if (projectIdToEdit) {
        await fetchAndUpdateProjectData(projectIdToEdit, tabIndex);
      }
      setActiveTab(tabIndex);
      const element = document.getElementById('project-builder');
      if (element) {
        window.scrollTo({
          top: element.offsetTop,
          behavior: 'smooth',
        });
      }
    },
    [projectIdToEdit],
  );

  useEffect(() => {
    isFormDirtyRef.current = isFormDirty;
    openSaveConfirmationRef.current = openSaveConfirmation;
  }, [isFormDirty, openSaveConfirmation]);

  const tabs = useMemo(
    () => [
      {
        id: 1,
        label: 'Project details',
        content: (
          <ProjectDetails
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabClick={handleTabClick}
            projectFormData={projectFormData}
            setIsFormDirty={setIsFormDirty}
            isFormDirty={isFormDirty}
            openSaveConfirmation={openSaveConfirmation}
            setOpenSaveConfirmation={setOpenSaveConfirmation}
            handleCancelLeave={handleCancelLeave}
            handleConfirmLeave={handleConfirmLeave}
            tabIndexValue={tabIndexValue}
          />
        ),
        disabled: false,
      },
      { id: 2, label: 'Add products', content: <AddProducts />, disabled: !projectFormData },
      {
        id: 3,
        label: 'View products',
        content: (
          <ViewProducts
            projectForm={projectFormData}
            handleTabClick={handleTabClick}
            setIsFormDirty={setIsFormDirty}
            openSaveConfirmation={openSaveConfirmation}
            setOpenSaveConfirmation={setOpenSaveConfirmation}
            tabIndexValue={tabIndexValue}
            setTabIndexValue={setTabIndexValue}
            handleConfirmLeave={handleConfirmLeave}
            handleCancelLeave={handleCancelLeave}
          />
        ),
        disabled: isCreatePage,
      },
      {
        id: 4,
        label: 'Preview',
        content: (
          <ProjectSummary projectFormData={projectFormData} handleTabClick={handleTabClick} data={data?.data_banner} />
        ),
        disabled: isCreatePage,
      },
    ],
    [activeTab, projectFormData, openSaveConfirmation],
  );

  useEffect(() => {
    if (projectIdToEdit) {
      handleTabClick(isViewPage ? 3 : 1);
    }
  }, []);

  if (loading || ((isEditPage || isViewPage) && projectIdToEdit && !projectFormData)) {
    return <Spinner />;
  }

  const createProject = () => {
    return (
      <div className={`${!isDesktopSize ? 'mobile' : 'desktop'}`}>
        <Typography className="fontSize32-sm !mb-20 whitespace-normal break-words text-black">
          {projectTitle}
        </Typography>
        {isDesktopSize && (
          <>
            <div className="tab-row">
              {tabs.map((tab, index) => (
                <div key={tab.id} className="tab-item">
                  <button
                    className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => handleTabClick(tab.id)}
                    disabled={tab.disabled}
                  >
                    <span className={`fontSize18-sm circle ${activeTab === tab.id ? 'active-circle' : ''}`}>
                      {index + 1}
                    </span>
                    <p className="fontSize18-sm">{tab.label}</p>
                  </button>
                  {index < tabs.length - 1 && <div className="line horizontal-line"></div>}
                </div>
              ))}
            </div>
            <div className="tab-content">{tabs.find((tab) => tab.id === activeTab)?.content}</div>
          </>
        )}
        {!isDesktopSize &&
          tabs.map((tab, index) => (
            <div key={tab.id} className="tab-section">
              <div className="tab-heading">
                <button
                  className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabClick(tab.id)}
                  disabled={tab.disabled}
                >
                  <span className={`circle ${activeTab === tab.id ? 'active-circle' : ''}`}>{index + 1}</span>
                  {tab.label}
                </button>
                <div className="inline-flex w-full">
                  <div className={`${activeTab === tab.id ? 'vertical-line-active' : 'vertical-line'}`}></div>
                  {activeTab === tab.id && <div className="tab-content indent-content w-full">{tab.content}</div>}
                </div>
              </div>
            </div>
          ))}
      </div>
    );
  };

  return !loggedIn ? (
    <Spinner />
  ) : (
    <>
      {isMediumDesktop ? (
        <ProjectLayout>{createProject()}</ProjectLayout>
      ) : (
        <div className={`p-[22px] md:p-[60px]`} id="project-builder">
          {createProject()}
        </div>
      )}
    </>
  );
};

export default ProjectBuilder;
